
import { Component, Vue } from "vue-property-decorator";
import {
  PARTNERS_FETCH,
  PARTNERS_CREATE,
  PARTNERS_EDIT,
  PARTNERS_REMOVE,
  TRANSLATIONS_FETCH,
  PARTNERS_REDIRECT
} from "@/store/ActionTypes";

import { mapState } from "vuex";
import ListRequest, { Filter, Ordering } from "@/models/ListRequest";
import Partner from "@/models/Partner/Partner";
import NewPartner from "@/models/Partner/NewPartner";
import UpdatePartner from "@/models/Partner/UpdatePartner";

import vTable from "@/components/vTable.vue";

import vModal from "@/components/vModal.vue";

@Component({
  components: {
    vTable,
    vModal
  },
  computed: {
    ...mapState({
      partnersState: (state: any) => state.PartnersModule,
      localizationState: (state: any) => state.LocalizationModule
    })
  }
})
class Partners extends Vue {
  partnersState: any;
  localizationState: any;

  // vTable props
  selectErase = true;
  searchable = true;
  editableTable = true;
  loading = false;

  // modals props
  showModal = false;
  showRemovePrompt = false;
  removePromptTitle = "";
  modalTitle = "";

  // self explainatory
  editMode = false;

  // List of id of partners to be removed
  removeList: number[] = [];

  // models
  newPartner: NewPartner = {
    name: "",
    address: "",
    phone: "",
    email: ""
  };

  partner: Partner = {
    id: 0,
    name: "",
    address: "",
    phone: "",
    email: "",
    couponsCount: 0
  };

  updatePartner: UpdatePartner = {
    id: 0,
    name: "",
    address: "",
    phone: "",
    email: ""
  };

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });
    this.loading = true;
    this.$store
      .dispatch(
        PARTNERS_FETCH,
        new ListRequest({
          page: args[0],
          pageSize: 25,
          filters: args[2],
          orders: orderings
        })
      )
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.loading = true;
    this.$store
      .dispatch(
        PARTNERS_FETCH,
        new ListRequest({
          page: 1,
          pageSize: this.partnersState.pageSize,
          filters,
          orders: orderings
        })
      )
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.loading = true;
    this.$store
      .dispatch(
        PARTNERS_FETCH,
        new ListRequest({
          page: this.partnersState.page,
          pageSize: this.partnersState.pageSize,
          filters,
          orders: orderings
        })
      )
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Method that resets most variables and closes all modals.
   * Fetching customers for update
   */
  resetState() {
    this.showModal = false;
    this.editMode = false;
    this.modalTitle = this.translations.get("partners_modal_title_add");

    this.newPartner = {
      name: "",
      address: "",
      phone: "",
      email: ""
    };

    this.partner = {
      id: 0,
      name: "",
      address: "",
      phone: "",
      email: "",
      couponsCount: 0
    };

    this.updatePartner = {
      id: 0,
      name: "",
      address: "",
      phone: "",
      email: ""
    };

    this.showRemovePrompt = false;
    this.removePromptTitle = "";
  }

  /**
   * Method for edit / add partner modal toggling
   */
  toggleModal() {
    this.showModal = !this.showModal;
    if (this.editMode) {
      this.modalTitle = this.translations.get("partners_modal_title_edit");
    }
    else {
      this.modalTitle = this.translations.get("partners_modal_title_add");
    }
  }

  /**
   * Getter method to transform the list of partners to grid data (vTable)
   */
  get transformPartners() {
    return this.partnersState.partnerList.map(
      (element: Partner) => {
        return {
          e1: element.address,
          e2: element.phone,
          e3: element.email,
          e4: {
            component: "button",
            props: {
              id: element.id,
              class: "btn btn-primary",
              style: "width: 100%;",
              disabled: !(element.couponsCount > 0)
            },
            html: element.couponsCount,
            onClick: (id: number) => this.viewCoupons(id)
          },
          meta: {
            id: element.id,
            link: element.name
          }
        };
      }
    );
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("partners_name"),
        key: "Name",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("address"),
        key: "Address",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("phone"),
        key: "Phone",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("email"),
        key: "Email",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons"),
        key: "Coupons",
        searchable: false,
        searchString: "",
        sortable: true
      }
    ];
  }

  /**
   * Populating form and toggling the modal for editing partner.
   * Sets the edit mode to true
   *
   * @param {number} id
   */
  prepareEditMode(id: number) {
    this.partner = this.partnersState.partnerList.find(
      (partner: Partner) => partner.id === id
    );
    this.updatePartner = {
      id: this.partner.id,
      email: this.partner.email,
      phone: this.partner.phone,
      address: this.partner.address,
      name: this.partner.name
    };

    this.editMode = true;
    this.modalTitle = this.translations.get("partners_modal_title_edit");
    this.toggleModal();
  }

  /**
   * Method for displaying prompt message for selected partners to be deleted
   *
   * @param {number[]} ids
   */
  prepareDeletePrompt(ids: number[]) {
    this.removeList = ids;
    this.removePromptTitle = this.translations.get(
      "partners_delete_prompt_message",
      ids.length
    );
    this.showRemovePrompt = true;
  }

  /**
   * Method for deleting selected partners
   */
  confirmDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("partners_toastr_deleting", this.removeList.length),
      () =>
        new Promise((resolve, reject) => {
          this.$store
            .dispatch(PARTNERS_REMOVE, { ids: this.removeList })
            .then(() => {
              resolve({
                body: this.translations.get(
                  "partners_toastr_deleted",
                  this.removeList.length
                )
              });
              this.loading = true;
              this.$store
                .dispatch(
                  PARTNERS_FETCH,
                  new ListRequest({ page: 1, pageSize: 25 })
                )
                .then(() => {
                  this.loading = false;
                })
                .catch(() => {
                  this.loading = false;
                });
              this.resetState();
              this.removeList = [];
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              this.resetState();
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
  }

  /**
   * Router method for submitting to API
   */
  submit() {
    if (this.editMode) {
      this.submitUpdate();
    }
    else {
      this.submitNew();
    }
  }

  /**
   * Method for submitting edit to api and showing toastr
   */
  submitUpdate() {
    this.$validator.validateAll().then((result) => {
      if (result) {
        if (
          this.partner.name !== this.updatePartner.name ||
          this.partner.address !== this.updatePartner.address ||
          this.partner.phone !== this.updatePartner.phone ||
          this.partner.email !== this.updatePartner.email
        ) {
          // @ts-ignore
          this.$snotify.async(
            this.translations.get("partners_toastr_updating"),
            () =>
              new Promise((resolve, reject) => {
                this.$store
                  .dispatch(PARTNERS_EDIT, this.updatePartner)
                  .then(() => {
                    resolve({
                      body: this.translations.get(
                        "partners_toastr_updated",
                        this.updatePartner.name
                      )
                    });
                    this.loading = true;
                    this.$store
                      .dispatch(
                        PARTNERS_FETCH,
                        new ListRequest({
                          page: this.partnersState.page,
                          pageSize: this.partnersState.pageSize,
                          filters: this.partnersState.filters,
                          orders: this.partnersState.orders
                        })
                      )
                      .then(() => {
                        this.loading = false;
                      })
                      .catch(() => {
                        this.loading = false;
                      });
                    this.resetState();
                  })
                  .catch((error) => {
                    let message = "";
                    if (error.response) {
                      message =
                        error.response.data.errorMessages[0].userVisibleMessage;
                    }
                    else {
                      message = this.translations.get("network_error");
                    }
                    reject({ body: message });
                  });
              }),
            {
              timeout: 3000,
              closeOnClick: true
            }
          );
        }
        else {
          this.$snotify.simple(this.translations.get("toastr_nochanges"));
          this.resetState();
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for submitting new to api and showing toastr
   */
  submitNew() {
    this.$validator.validateAll().then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("partners_toastr_adding"),
          () =>
            new Promise((resolve, reject) => {
              this.$store
                .dispatch(PARTNERS_CREATE, this.newPartner)
                .then(() => {
                  resolve({
                    body: this.translations.get(
                      "partners_toastr_added",
                      this.newPartner.name
                    )
                  });
                  this.loading = true;
                  this.$store
                    .dispatch(
                      PARTNERS_FETCH,
                      new ListRequest({
                        page: this.partnersState.page,
                        pageSize: this.partnersState.pageSize,
                        filters: this.partnersState.filters,
                        orders: this.partnersState.orders
                      })
                    )
                    .then(() => {
                      this.loading = false;
                    })
                    .catch(() => {
                      this.loading = false;
                    });
                  this.resetState();
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                  reject({ body: message });
                });
            }),
          {
            timeout: 3000,
            closeOnClick: true
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for redirecting to pre-filtered Coupons view for the chosen partner
   * with partnerId
   *
   * @param {number} partnerId
   */
  viewCoupons(partnerId: number) {
    this.$store
      .dispatch(
        PARTNERS_REDIRECT,
        new ListRequest({
          page: 1,
          pageSize: 25,
          filters: [{ name: "PartnerId", value: partnerId + "" }]
        })
      )
      .then(() => {
        this.$router.push("coupons");
      });
  }

  mounted() {
    this.loading = true;
    this.$store
      .dispatch(
        PARTNERS_FETCH,
        new ListRequest({ page: 1, pageSize: this.partnersState.pageSize })
      )
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["partners", "common"]
    });

    document.title = "Partners | MenuSano";
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default Partners;
